import type { ReactNode } from "react";

import type { UPSSizingResponse } from "../services/upsSizing";
import type { UPSEnergyBasis, UPSSizingWarningCode } from "../services/upsSizingContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { SCENARIO_LABELS } from "./LoadRow";
import { GOVERNING_BASIS_LABELS, REDUNDANCY_LABELS, STATUS_LABELS } from "./UPSResultSummary";
import {
  UPS_BATTERY_TECHNOLOGY_LABELS,
  UPS_PHASE_CONFIGURATION_LABELS,
  UPS_TOPOLOGY_LABELS,
} from "./upsStudyDraft";

interface ValueRow {
  key: string;
  label: string;
  // Decimal values are exact backend strings: four significant figures on
  // screen, the exact value in the tooltip (display rule A12). The unit is in
  // the row label. null renders as an em dash.
  value: string | null;
  // The warnings that say this figure was assumed rather than established. A
  // single input names its own code; the combined derating factor names both,
  // because either one makes the product an assumption too.
  notEstablishedCodes?: readonly UPSSizingWarningCode[];
  // What follows the figure when one of those warnings is present.
  assumedSuffix?: string;
  // A percentage keeps its sign with the figure, as the DG loading row does.
  unit?: string;
}

interface UPSResultPanelProps {
  result: UPSSizingResponse;
}

const NOT_ESTABLISHED_SUFFIX = " (not established)";
const INCLUDES_NOT_ESTABLISHED_SUFFIX = " (includes a not-established factor)";

/**
 * The two loading figures are not the running load over the nameplate: each is
 * a requirement over the derated installed duty capacity of the same kind. The
 * labels say so in full (A18 (c)).
 */
export const LOADING_KW_LABEL =
  "Loading, kW (design kW incl. growth and margin ÷ derated installed duty capacity kW)";
export const LOADING_KVA_LABEL =
  "Loading, kVA (kVA requirement incl. growth and margin ÷ derated installed duty capacity kVA)";

/** Carried by every result (A18 (e)): what the energy figures do not cover. */
export const BATTERY_NOTE =
  "Cell count, Ah, end-of-discharge voltage, ageing and temperature correction are not " +
  "evaluated (GAP-018).";

// The energy basis is stated on every result; a new basis without words is a
// compile error.
const ENERGY_BASIS_LABELS: Record<UPSEnergyBasis, string> = {
  DESIGN_KW_INCLUDING_GROWTH_AND_MARGIN: "Design kW including growth and margin",
};

// The two factors the combined derating factor is the product of.
const DERATING_CODES = [
  "AMBIENT_DERATING_NOT_ESTABLISHED",
  "ALTITUDE_DERATING_NOT_ESTABLISHED",
] as const satisfies readonly UPSSizingWarningCode[];

// The calculation ladder, in the engine's order: critical load, growth and margin
// to the design kW, the two limits (kVA at the load power factor, kW at the UPS
// rated output power factor), the larger of the two, and derating to the
// capacity the schedule must cover.
function loadRows(result: UPSSizingResponse): ValueRow[] {
  return [
    { key: "critical_load_kw", label: "Critical load (kW)", value: result.critical_load_kw },
    { key: "load_power_factor", label: "Load power factor", value: result.load_power_factor },
    {
      key: "future_growth_factor",
      label: "Future growth factor",
      value: result.future_growth_factor,
      notEstablishedCodes: ["GROWTH_FACTOR_NOT_ESTABLISHED"],
    },
    {
      key: "design_margin_factor",
      label: "Design margin factor",
      value: result.design_margin_factor,
      notEstablishedCodes: ["DESIGN_MARGIN_NOT_ESTABLISHED"],
    },
    {
      key: "design_load_kw",
      label: "Design load, critical load × growth × margin (kW)",
      value: result.design_load_kw,
    },
    {
      key: "kva_requirement_kva",
      label: "kVA requirement, design kW ÷ load power factor (kVA)",
      value: result.kva_requirement_kva,
    },
    {
      key: "ups_rated_output_power_factor",
      label: "UPS rated output power factor",
      value: result.ups_rated_output_power_factor,
    },
    {
      key: "active_power_requirement_kva",
      label: "Active-power requirement, design kW ÷ UPS rated output power factor (kVA)",
      value: result.active_power_requirement_kva,
    },
  ];
}

function requirementRows(result: UPSSizingResponse): ValueRow[] {
  return [
    {
      key: "governing_requirement_kva",
      label: "Governing requirement (kVA)",
      value: result.governing_requirement_kva,
    },
    {
      key: "combined_derating_factor",
      label: "Combined derating factor",
      value: result.combined_derating_factor,
      notEstablishedCodes: DERATING_CODES,
      assumedSuffix: INCLUDES_NOT_ESTABLISHED_SUFFIX,
    },
    {
      key: "derated_required_capacity_kva",
      label: "Derated requirement (kVA)",
      value: result.derated_required_capacity_kva,
    },
  ];
}

function capacityRows(result: UPSSizingResponse): ValueRow[] {
  return [
    {
      key: "installed_nameplate_capacity_kva",
      label: "Installed capacity (kVA)",
      value: result.installed_nameplate_capacity_kva,
    },
    {
      key: "derated_duty_capacity_kva",
      label: "Derated installed duty capacity (kVA)",
      value: result.derated_duty_capacity_kva,
    },
    {
      key: "derated_duty_capacity_kw",
      label: "Derated installed duty capacity (kW)",
      value: result.derated_duty_capacity_kw,
    },
    {
      key: "spare_derated_capacity_kva",
      label: "Spare derated capacity (kVA)",
      value: result.spare_derated_capacity_kva,
    },
    // The long labels leave no room for a unit, so the percent sign stays with the figure.
    {
      key: "loading_kw_percent",
      label: LOADING_KW_LABEL,
      value: result.loading_kw_percent,
      unit: "%",
    },
    {
      key: "loading_kva_percent",
      label: LOADING_KVA_LABEL,
      value: result.loading_kva_percent,
      unit: "%",
    },
  ];
}

function energyRows(result: UPSSizingResponse): ValueRow[] {
  return [
    {
      key: "required_runtime_minutes",
      label: "Required runtime (min)",
      value: result.required_runtime_minutes,
    },
    {
      key: "inverter_efficiency",
      label: "Inverter efficiency",
      value: result.inverter_efficiency,
      notEstablishedCodes: ["INVERTER_EFFICIENCY_NOT_ESTABLISHED"],
    },
  ];
}

function ValueRows({
  rows,
  warningCodes,
}: {
  rows: ValueRow[];
  warningCodes: ReadonlySet<string>;
}) {
  return (
    <>
      {rows.map((row) => {
        const quantity = formatQuantity(row.value, row.unit);
        // An input is always a figure; only the warning says whether the
        // engineer established it (A16 (a), A18 (f)). The tooltip keeps the
        // exact value either way.
        const assumed = row.notEstablishedCodes?.some((code) => warningCodes.has(code)) ?? false;
        const suffix = row.assumedSuffix ?? NOT_ESTABLISHED_SUFFIX;
        return (
          <tr key={row.key} data-row-key={row.key}>
            <th scope="row">{row.label}</th>
            <td title={quantity.exact ?? undefined}>
              {assumed ? `${quantity.display}${suffix}` : quantity.display}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

export function UPSResultPanel({ result }: UPSResultPanelProps) {
  const warningCodes = new Set<string>(result.warnings.map((warning) => warning.code));

  return (
    <article aria-label="UPS sizing result">
      <header>
        <h2>UPS sizing result</h2>
        <p>
          Study code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status:{" "}
          <strong data-result-status={result.status}>{STATUS_LABELS[result.status]}</strong>
        </p>
      </header>

      <ValueTable title="Load and design">
        <ValueRows rows={loadRows(result)} warningCodes={warningCodes} />
        <tr data-row-key="governing_basis">
          <th scope="row">Governing basis</th>
          {/* Stated by the engine (A18 (c)), never recomputed here. */}
          <td data-governing-basis={result.governing_basis}>
            {GOVERNING_BASIS_LABELS[result.governing_basis]}
          </td>
        </tr>
        <ValueRows rows={requirementRows(result)} warningCodes={warningCodes} />
      </ValueTable>

      <ValueTable title="Selection">
        <tr data-row-key="required_unit_rating_kva">
          <th scope="row">Required module rating, after derating (kVA)</th>
          <td title={formatQuantity(result.required_unit_rating_kva).exact ?? undefined}>
            {formatQuantity(result.required_unit_rating_kva).display}
          </td>
        </tr>
        <tr data-row-key="selected_unit_rating_kva">
          <th scope="row">Selected module rating (kVA)</th>
          {/* A catalogue value from the project design basis: shown as
              sent, so the engineer reads back an orderable rating. */}
          <td>{result.selected_unit_rating_kva ?? EMPTY_QUANTITY}</td>
        </tr>
        <tr data-row-key="available_unit_ratings_kva">
          <th scope="row">Rating schedule considered (kVA)</th>
          {/* The schedule the engine chose from, exactly as it was sent: the
              evidence shows which ratings were on the table. */}
          <td>{result.available_unit_ratings_kva.join(" / ")}</td>
        </tr>
        <tr data-row-key="duty_modules">
          <th scope="row">Duty modules</th>
          {/* Counts, not engineering quantities. */}
          <td>{result.duty_modules}</td>
        </tr>
        <tr data-row-key="redundant_modules">
          <th scope="row">Redundant modules</th>
          <td>{result.redundant_modules}</td>
        </tr>
        <tr data-row-key="total_modules">
          <th scope="row">Total modules</th>
          <td>{result.total_modules}</td>
        </tr>
        <tr data-row-key="redundancy_mode">
          <th scope="row">Redundancy mode</th>
          <td data-redundancy-mode={result.redundancy_mode}>
            {REDUNDANCY_LABELS[result.redundancy_mode]}
          </td>
        </tr>
        <ValueRows rows={capacityRows(result)} warningCodes={warningCodes} />
      </ValueTable>

      <ValueTable title="Battery energy">
        <ValueRows rows={energyRows(result)} warningCodes={warningCodes} />
        <tr data-row-key="energy_basis">
          <th scope="row">Energy basis</th>
          <td data-energy-basis={result.energy_basis}>
            {ENERGY_BASIS_LABELS[result.energy_basis]}
          </td>
        </tr>
        <ValueRows
          rows={[
            { key: "load_energy_kwh", label: "Load energy (kWh)", value: result.load_energy_kwh },
            {
              key: "battery_dc_energy_kwh",
              label: "Battery DC energy, load energy ÷ inverter efficiency (kWh)",
              value: result.battery_dc_energy_kwh,
            },
          ]}
          warningCodes={warningCodes}
        />
      </ValueTable>
      <p data-battery-note="true">{BATTERY_NOTE}</p>

      <ValueTable title="Descriptors">
        {/* Recorded with the study; none of them changes a calculated value (A18 (h)). */}
        <tr data-row-key="topology">
          <th scope="row">Topology</th>
          <td data-topology={result.topology ?? undefined}>
            {result.topology === null ? EMPTY_QUANTITY : UPS_TOPOLOGY_LABELS[result.topology]}
          </td>
        </tr>
        <tr data-row-key="phase_configuration">
          <th scope="row">Phase configuration</th>
          <td data-phase-configuration={result.phase_configuration ?? undefined}>
            {result.phase_configuration === null
              ? EMPTY_QUANTITY
              : UPS_PHASE_CONFIGURATION_LABELS[result.phase_configuration]}
          </td>
        </tr>
        <tr data-row-key="battery_technology">
          <th scope="row">Battery technology</th>
          <td data-battery-technology={result.battery_technology ?? undefined}>
            {result.battery_technology === null
              ? EMPTY_QUANTITY
              : UPS_BATTERY_TECHNOLOGY_LABELS[result.battery_technology]}
          </td>
        </tr>
        <tr data-row-key="scenario">
          <th scope="row">Scenario</th>
          <td data-scenario={result.scenario ?? undefined}>
            {result.scenario === null ? EMPTY_QUANTITY : SCENARIO_LABELS[result.scenario]}
          </td>
        </tr>
      </ValueTable>

      <section aria-label="Jurisdiction profile">
        <h3>Jurisdiction profile</h3>
        <p data-jurisdiction-profile={result.jurisdiction_profile}>
          Designed under the {result.jurisdiction_profile} profile. The sizing itself carries
          no profile-dependent value; the profile is recorded with the run and must match the
          design basis of the project revision.
        </p>
      </section>

      <footer>
        <p>
          Calculation results are engineering design checks, not a statutory compliance
          declaration. Confirm the governing project basis, the critical load, the required
          runtime, the inverter efficiency, the UPS rated output power factor, the rating
          schedule and every growth, margin and derating factor before approval or issue.
          Battery sizing is not evaluated (GAP-018).
        </p>
      </footer>
    </article>
  );
}

export default UPSResultPanel;
